import { Injectable } from '@angular/core';
import { SentimentScaleAPIService } from '../api/sentiment-scale-api.service';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { CreateSentimentScale } from '../model/create-sentiment-scale.model';
import { SentimentScale } from '../model/sentiment-scale.model';
import { SentimentScaleMessages } from '../locale/sentiment-scale.messages';
import { map } from 'rxjs/operators';
import { FrankliHttpErrorResponse } from '@app/models/exception/frankli-http-error-response.model';
import { SwalUtils } from '@app/shared/utils/swal.utils';
import { ErrorCode } from '@app/models/exception/error-codes.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';


@Injectable({
  providedIn: 'root'
})
export class SentimentScaleBusinessService {

  // key -> the write currently in flight for that scale. A duplicate call while one is in flight is
  // coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private static _updatingScales: InFlightRegistry;

  constructor(
    private sentimentScaleAPIService: SentimentScaleAPIService,
    private notifyUtils: NotifyUtils,
    private swalUtils: SwalUtils
  ) {
    SentimentScaleBusinessService._updatingScales = new InFlightRegistry();
  }

  public isModifyingScale(scheduleId: number): boolean {
    return SentimentScaleBusinessService._updatingScales.has(scheduleId);
  }

  create(createDto: CreateSentimentScale): Promise<SentimentScale> {
    return firstValueFrom(this.sentimentScaleAPIService.create(createDto))
      .then(res => SentimentScaleBusinessService.parseSentimentScale(res))
      .then(res => {
        this.notifyUtils.notify(SentimentScaleMessages.CREATE_SUCCESS_TOAST);
        return res;
      });
  }

  update(id: number, updateDto: CreateSentimentScale): Promise<SentimentScale> {
    return SentimentScaleBusinessService._updatingScales.run(id, () =>
      firstValueFrom(this.sentimentScaleAPIService.update(id, updateDto, false))
        .then(res => SentimentScaleBusinessService.parseSentimentScale(res))
        .then(res => {
          this.notifyUtils.notify(SentimentScaleMessages.EDIT_SUCCESS_TOAST);
          return res;
        })
        .catch((err: FrankliHttpErrorResponse) => {
          if (err?.error?.errorCode === ErrorCode.SENTIMENT_SCALE_CANNOT_MODIFY_BECAUSE_USED) {
            // Show confirmation modal asking if they want to update the sentiment scale anyways and explaining what will happen if they continue
            return this.swalUtils.displayWarningConfirmationSwal({
              title: 'This sentiment scale is already in use. Would you like to replace it?',
              text: 'This will archive the current version and create a copy with all the changes made. Any schedules using this scale will be updated to use te new scale.'
            }).then(res => {
              // Finish running quietly and swallow the previous error
              if (!res.isConfirmed) {
                return null;
              }

              return firstValueFrom(this.sentimentScaleAPIService.update(id, updateDto, true))
                .then(resInner => SentimentScaleBusinessService.parseSentimentScale(resInner))
                .then(resInner => {
                  this.notifyUtils.notify(SentimentScaleMessages.EDIT_SUCCESS_TOAST);
                  return resInner;
                })
                .catch(errInner => {
                  console.error('Failed to update sentiment scale');
                  throw errInner;
                });
            });
          } else {
            throw err;
          }
        })
    );
  }

  archive(id: number): Promise<SentimentScale> {
    return SentimentScaleBusinessService._updatingScales.run(id, () =>
      firstValueFrom(this.sentimentScaleAPIService.archive(id))
        .then(res => SentimentScaleBusinessService.parseSentimentScale(res))
        .then(res => {
          this.notifyUtils.notify(SentimentScaleMessages.ARCHIVE_SUCCESS_TOAST);
          return res;
        })
    );
  }

  unarchive(id: number): Promise<SentimentScale> {
    return SentimentScaleBusinessService._updatingScales.run(id, () =>
      firstValueFrom(this.sentimentScaleAPIService.unarchive(id))
        .then(res => SentimentScaleBusinessService.parseSentimentScale(res))
        .then(res => {
          this.notifyUtils.notify(SentimentScaleMessages.UNARCHIVE_SUCCESS_TOAST);
          return res;
        })
    );
  }

  getAll(_includeArchived?: boolean): Observable<SentimentScale[]> {
    return this.sentimentScaleAPIService.getAll(_includeArchived)
      .pipe(map(scales => scales.map(SentimentScaleBusinessService.parseSentimentScale)));
  }

  getOne(id: number): Observable<SentimentScale> {
    return this.sentimentScaleAPIService.getById(id)
      .pipe(map(SentimentScaleBusinessService.parseSentimentScale));
  }

  getByIdIn(ids: number[]): Observable<SentimentScale[]> {
    return this.sentimentScaleAPIService.getByIdIn(ids)
      .pipe(map(scales => scales.map(SentimentScaleBusinessService.parseSentimentScale)));
  }

  public static parseSentimentScale(sentimentScale: SentimentScale): SentimentScale {
    // Sort options by orderIndex
    sentimentScale.options = sentimentScale.options.sort((a, b) => a.orderIndex - b.orderIndex);

    return sentimentScale;
  }
}
